import { zodResolver } from '@hookform/resolvers/zod';
import type { HTMLProps } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '~/components/Button';
import { FormFieldInput } from '~/components/FormField';
import { FormFieldToggle } from '~/components/FormField/components/FormFieldToggle';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { AccordionContentBox } from '~/modules/settings/components/AccordionContentBox';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';

import type { LabelFormSchema } from './schema';
import { labelFormSchema } from './schema';

type LabelFormProps = {
  values?: LabelFormSchema;
  onSubmit: (data: LabelFormSchema) => void;
  isEditing?: boolean;
} & Omit<HTMLProps<HTMLFormElement>, 'method' | 'onSubmit'>;

export function LabelForm({
  values,
  onSubmit,
  isEditing = false,
  className,
  ...formProps
}: LabelFormProps) {
  const { t } = useTranslation('settings');

  const form = useForm<LabelFormSchema>({
    defaultValues: values,
    resolver: zodResolver(labelFormSchema, {
      errorMap: getI18nErrorMap((s) => t(`label.required.${s}`)),
    }),
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        {...formProps}
        className={className}
        data-testid="label-form"
      >
        <div className="flex flex-col gap-md">
          <AccordionContentBox title={t('label.basicInformation')}>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                name="labelName"
                className="min-w-xs flex-1"
                label={t('label.labelName')}
                placeholder={t('label.placeholder.labelName')}
              />
              <FormFieldInput
                name="parentLabel"
                className="min-w-xs flex-1"
                label={t('label.parentLabel')}
                placeholder={t('label.placeholder.parentLabel')}
              />
              <FormFieldInput
                name="labelGroups"
                className="min-w-xs flex-1"
                label={t('label.labelGroups')}
                placeholder={t('label.placeholder.labelGroups')}
              />
            </div>
          </AccordionContentBox>

          <AccordionContentBox title={t('label.coreLabelTitle')}>
            <div className="flex flex-col gap-md">
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="weaLabelCode"
                  label={t('label.weaLabelCode')}
                  placeholder={t('label.placeholder.weaLabelCode')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="usLabelCode"
                  label={t('label.usLabelCode')}
                  placeholder={t('label.placeholder.usLabelCode')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="dealOwner"
                  label={t('label.dealOwner')}
                  placeholder={t('label.placeholder.dealOwner')}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="dealRegionScope"
                  label={t('label.dealRegionScope')}
                  placeholder={t('label.placeholder.dealRegionScope')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="wmiLabelCode"
                  label={t('label.wmiLabelCode')}
                  placeholder={t('label.placeholder.wmiLabelCode')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="marketingCompany"
                  label={t('label.marketingCompany')}
                  placeholder={t('label.placeholder.marketingCompany')}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="repertoireOwner"
                  label={t('label.repertoireOwner')}
                  placeholder={t('label.placeholder.repertoireOwner')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="wbsElementNumber"
                  label={t('label.wbsElementNumber')}
                  placeholder={t('label.placeholder.wbsElementNumber')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="dealId"
                  label={t('label.dealId')}
                  placeholder={t('label.placeholder.dealId')}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="scopeId"
                  label={t('label.scopeId')}
                  placeholder={t('label.placeholder.scopeId')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="dealScope"
                  label={t('label.dealScope')}
                  placeholder={t('label.placeholder.dealScope')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="gpiNumber"
                  label={t('label.gpiNumber')}
                  placeholder={t('label.placeholder.gpiNumber')}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="profitCenter"
                  label={t('label.profitCenter')}
                  placeholder={t('label.placeholder.profitCenter')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="pmo"
                  label={t('label.pmo')}
                  placeholder={t('label.placeholder.pmo')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="gvlCode"
                  label={t('label.gvlCode')}
                  placeholder={t('label.placeholder.gvlCode')}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="isrcPrefix"
                  label={t('label.isrcPrefix')}
                  placeholder={t('label.placeholder.isrcPrefix')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="presentationLabel"
                  label={t('label.presentationLabel')}
                  placeholder={t('label.placeholder.presentationLabel')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="distributors"
                  label={t('label.distributors')}
                  placeholder={t('label.placeholder.distributors')}
                />
              </div>
            </div>
          </AccordionContentBox>

          <AccordionContentBox title={t('label.settings')}>
            <div className="grid grid-cols-3 gap-md rounded-sm">
              <div className="flex flex-col gap-md">
                <div className="flex gap-md">
                  <FormFieldToggle
                    label={t('label.directPass')}
                    name="directPass"
                    aria-label={t('label.directPass')}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-md">
                <div className="flex gap-md">
                  <FormFieldToggle
                    label={t('label.allowTimedReleases')}
                    name="allowTimedReleases"
                    aria-label={t('label.allowTimedReleases')}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-md">
                <div className="flex gap-md">
                  <FormFieldToggle
                    label={t('label.asylum')}
                    name="asylum"
                    aria-label={t('label.asylum')}
                  />
                </div>
              </div>
            </div>
          </AccordionContentBox>

          <AccordionContentBox title={t('label.distributionGroups')}>
            <div className="flex w-full flex-col items-center gap-lg rounded-sm px-md py-2xl bg-level-03">
              <span className="text-body-md font-semibold leading-tight">
                {t('label.addDistributionGroup')}
              </span>
              <Button
                variant="secondary"
                size="small"
                className="font-semibold"
              >
                {t('label.createGroup')}
              </Button>
            </div>
          </AccordionContentBox>

          <AccordionContentBox title={t('label.social')} className="mb-xl">
            <div className="flex flex-col gap-md">
              <div className="flex flex-wrap justify-between gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="facebook"
                  label={t('label.facebook')}
                  placeholder={t('label.placeholder.facebook')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="instagram"
                  label={t('label.instagram')}
                  placeholder={t('label.placeholder.instagram')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="youtube"
                  label={t('label.youtube')}
                  placeholder={t('label.placeholder.youtube')}
                />
              </div>
              <div className="flex flex-wrap gap-md rounded-sm">
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="x"
                  label={t('label.x')}
                  placeholder={t('label.placeholder.x')}
                />
                <FormFieldInput
                  className="min-w-xs flex-1"
                  name="websiteURL"
                  label={t('label.websiteURL')}
                  placeholder={t('label.placeholder.websiteURL')}
                />
              </div>
            </div>
          </AccordionContentBox>
        </div>
        <CTABlock>
          <Button asChild variant="secondary" size="large">
            <Link to={SETTINGS_ROUTES.LABEL_LIST}>
              {t('label.cancelButton')}
            </Link>
          </Button>
          <Button variant="primary" type="submit" size="large">
            {isEditing ? t('saveChanges') : t('label.addLink')}
          </Button>
        </CTABlock>
      </form>
    </FormProvider>
  );
}

LabelForm.displayName = 'LabelForm';
